/**
 * X-Axis component for time-based tick marks and labels.
 * Subscribes to 'scales' and 'viewport' events for updates.
 *
 * Uses core/tickGeneration for pure tick calculation logic.
 * This component is a thin visual wrapper.
 */

import React, { useMemo } from "react";

import styles from "../styles/styles.module.css";
import { useChartContext } from "../core/ChartContext";
import {
  computeSecondaryLabels,
  formatTimestamp,
  generateCompressedScaleTicks,
  generateNiceIntervalTicks,
  generateTimeAxisTicks,
  getIntradayXAxisTicks,
  getSecondaryLabelType,
  getTimePrecision,
  intervalToMs,
  intervalToPrecision,
} from "~utils/ticker-chart";
import type { ProcessedPricePoint } from "~types/types";
import { useChartSubscription } from "~hooks/ticker-chart";

export interface XAxisProps {
  /** Number of target ticks (actual count may vary) */
  tickCount?: number;
  /** Custom formatter function */
  formatter?: (timestamp: number) => string;
  /** Locale for date formatting */
  locale?: string;
  /** Show interval lines at each tick position, default: true */
  showIntervalLines?: boolean;
}

/**
 * X-Axis renders time tick marks and labels.
 * Updates imperatively when scales or viewport changes.
 */
const XAxisInner: React.FC<XAxisProps> = ({
  tickCount = 8,
  formatter,
  locale = "en-US",
  showIntervalLines = true,
}) => {
  const store = useChartContext();

  useChartSubscription(store, ["scales", "viewport"]);

  const layout = store.getLayout();
  const { x: xScale } = store.getScales();
  const { axisLabelSize, secondaryLabelSize, charWidth } = layout.typography;

  const state = store.getState();
  const interval = state.dataConfig?.interval;

  const { ticks, secondaryLabels, hasSecondaryLabels } = useMemo(() => {
    const domain = xScale.domain();
    const rangeMs = domain[1] - domain[0];
    const pixelRange: [number, number] = [
      layout.priceArea.x,
      layout.priceArea.x + layout.priceArea.width,
    ];

    // Get all gaps from the first series (gaps are the same across series)
    const seriesIds = Array.from(state.seriesMeta.keys());
    const firstSeriesId = seriesIds[0];
    const gaps = firstSeriesId ? store.getDataGaps(firstSeriesId) : [];
    const hasLargeGaps = gaps.some((g) => g.isLarge);

    // Determine tick generation strategy based on data characteristics
    let rawTicks: number[];

    const intraday = state.intraday;

    if (intraday && interval) {
      // Cumulative timeline ticks for 1D charts — collapses overnight gaps
      const dataIntervalMs = intervalToMs(interval);
      const seriesData = firstSeriesId
        ? store.getRenderableData(firstSeriesId)
        : [];
      const result = getIntradayXAxisTicks(
        seriesData as ProcessedPricePoint[],
        tickCount,
        dataIntervalMs
      );

      rawTicks = result.ticks;
    } else if (hasLargeGaps && interval) {
      // Use equidistant tick generation for compressed scale with gaps
      // This ensures ticks are visually evenly spaced and avoid falling in gaps
      const dataIntervalMs = intervalToMs(interval);
      const result = generateCompressedScaleTicks(
        xScale,
        pixelRange,
        tickCount,
        dataIntervalMs,
        gaps
      );

      rawTicks = result.ticks;
    } else if (interval && intervalToMs(interval) <= 60 * 60 * 1000) {
      // Use nice interval tick generation for intraday intervals <= 1 hour
      const result = generateNiceIntervalTicks(
        xScale,
        pixelRange,
        tickCount,
        intervalToMs(interval)
      );

      rawTicks = result.ticks;
    } else {
      // Fall back to pixel-based ticks for longer intervals or no interval config
      const result = generateTimeAxisTicks(xScale, pixelRange, tickCount);

      rawTicks = result.ticks;
    }

    // Use interval to derive precision when available, otherwise fall back to range
    const precision = interval
      ? intervalToPrecision(interval)
      : getTimePrecision(rangeMs);
    const secondaryLabelType = getSecondaryLabelType(precision);

    const chartLeft = layout.priceArea.x;
    const chartRight = layout.priceArea.x + layout.priceArea.width;

    const computedTicks = rawTicks
      .map((timestamp: number) => {
        const x = xScale.scale(timestamp);
        const label = formatter
          ? formatter(timestamp)
          : formatTimestamp(timestamp, precision, locale);

        // Estimate label width for visibility check
        const labelWidth = label.length * charWidth;
        const labelLeft = x - labelWidth / 2;
        const labelRight = x + labelWidth / 2;

        // Hide labels that would be clipped at edges
        const isVisible = labelLeft >= chartLeft && labelRight <= chartRight;

        return { timestamp, x, label, isVisible };
      })
      .filter((tick) => tick.isVisible);

    // Compute secondary labels using core function
    const computedSecondaryLabels = computeSecondaryLabels(
      computedTicks,
      secondaryLabelType,
      chartLeft,
      chartRight,
      locale
    );

    return {
      ticks: computedTicks,
      secondaryLabels: computedSecondaryLabels,
      hasSecondaryLabels: secondaryLabelType !== null,
    };
  }, [
    xScale,
    tickCount,
    formatter,
    locale,
    layout.priceArea,
    charWidth,
    interval,
    store,
    state.seriesMeta,
    state.intraday,
  ]);

  const axisY = layout.xAxisArea.y;
  const primaryLabelY = axisY + axisLabelSize + 6;
  const secondaryLabelY = primaryLabelY + secondaryLabelSize + 2;

  return (
    <g className={styles.xAxis} data-component="price-movement-chart-xaxis">
      {/* Axis line */}
      <line
        className={styles.axisLine}
        x1={layout.priceArea.x}
        y1={axisY}
        x2={layout.priceArea.x + layout.priceArea.width}
        y2={axisY}
        data-component="price-movement-chart-xaxis-line"
      />

      {/* Ticks with interval lines, tick marks, and labels */}
      {ticks.map(({ timestamp, x, label }) => (
        <g key={timestamp}>
          {/* Interval line (vertical grid line at tick position) */}
          {showIntervalLines && (
            <line
              className={styles.intervalLine}
              x1={x}
              y1={layout.priceArea.y}
              x2={x}
              y2={axisY}
              data-component="price-movement-chart-xaxis-grid-line"
            />
          )}

          {/* Tick mark */}
          <line
            className={styles.axisTickMark}
            x1={x}
            y1={axisY}
            x2={x}
            y2={axisY + 6}
            data-component="price-movement-chart-xaxis-tick-mark"
          />

          {/* Primary label */}
          <text
            className={styles.axisTick}
            x={x}
            y={primaryLabelY}
            textAnchor="middle"
            fontSize={axisLabelSize}
            data-component="price-movement-chart-xaxis-primary-label"
          >
            {label}
          </text>
        </g>
      ))}

      {/* Secondary labels (date or year context) */}
      {hasSecondaryLabels &&
        secondaryLabels.map(({ x, label, timestamp }, index) => (
          <text
            key={`secondary-${timestamp}-${index}`}
            className={`${styles.axisTick} ${styles.axisTickSecondary}`}
            x={x}
            y={secondaryLabelY}
            textAnchor="middle"
            fontSize={secondaryLabelSize}
            data-component="price-movement-chart-xaxis-secondary-label"
          >
            {label}
          </text>
        ))}
    </g>
  );
};

export const XAxis = React.memo(XAxisInner);
XAxis.displayName = "XAxis";
